import { Button, DataGrid, IconButton, Menu, StatusIndicator } from '@neo4j-ndl/react';
import { ArrowPathIconOutline, EllipsisHorizontalIconOutline } from '@neo4j-ndl/react/icons';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { DataGridHelpers, SearchField } from '@nx/ui';
import { createColumnHelper, getFilteredRowModel } from '@tanstack/react-table';
import { useMemo, useRef, useState } from 'react';

import { useCommonTable } from '../../../../../upx/src/components/table';
import { useModalState } from '../../../log-forwarding/hooks/use-modal-state';
import type { IndexToFix } from './fix-index';
import { FixIndexesModal } from './fix-index';

interface IndexesTableProps {
  oldIndexes: IndexToFix[];
  invalidateIndexesToFixCypherQuery: () => void;
  isLoading: boolean;
}

const FixAllIndexesButton = ({ oldIndexes, onFixIndex }: { oldIndexes: IndexToFix[]; onFixIndex: () => void }) => {
  const fixIndexModalState = useModalState();
  return (
    <>
      <FixIndexesModal
        key={fixIndexModalState.key}
        isOpen={fixIndexModalState.visible}
        oldIndexes={oldIndexes}
        onClose={() => {
          fixIndexModalState.close();
          onFixIndex();
        }}
      />
      <Button
        color="neutral"
        fill="outlined"
        onClick={() => {
          fixIndexModalState.refreshKey();
          fixIndexModalState.show();
        }}
      >
        Replace all
      </Button>
    </>
  );
};

const IndexTableActionCell = ({ oldIndex, onFixIndex }: { oldIndex: IndexToFix; onFixIndex: () => void }) => {
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isFixIndexModalOpen, setIsFixIndexModalOpen] = useState(false);

  return (
    <div>
      <FixIndexesModal
        oldIndexes={[oldIndex]}
        isOpen={isFixIndexModalOpen}
        onClose={() => {
          setIsFixIndexModalOpen(false);
          onFixIndex();
        }}
      />
      <div className="flex">
        <IconButton
          className="flex w-8 flex-row-reverse"
          isDisabled={isNotNullish(oldIndex.replacementName)}
          onClick={() => {
            setIsOpen(true);
          }}
          ref={anchorEl}
          ariaLabel={'Endpoint actions'}
          isClean={true}
        >
          <EllipsisHorizontalIconOutline />
        </IconButton>
      </div>
      <Menu isOpen={isOpen} onClose={() => setIsOpen(false)} anchorRef={anchorEl}>
        <Menu.Items>
          <Menu.Item
            title="View and replace"
            onClick={() => {
              setIsFixIndexModalOpen(true);
            }}
          />
        </Menu.Items>
      </Menu>
    </div>
  );
};

export const IndexesTable = ({ oldIndexes, invalidateIndexesToFixCypherQuery, isLoading }: IndexesTableProps) => {
  const [globalFilter, setGlobalFilter] = useState('');

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<IndexToFix>();
    return [
      columnHelper.accessor('name', {
        header: 'Index name',
      }),
      columnHelper.accessor('replacementName', {
        id: 'replacementName',
        header: 'Status',
        size: 300,
        cell: ({ row }) => {
          const isReplaced = isNotNullish(row.original.replacementName);
          return (
            <>
              <StatusIndicator type={isReplaced ? 'success' : 'danger'}></StatusIndicator>
              <span className="pl-2">
                {isReplaced ? `Replaced with ${row.original.replacementName}` : 'Not replaced'}
              </span>
            </>
          );
        },
      }),
      columnHelper.accessor('state', {
        header: 'State',
      }),
      columnHelper.accessor('type', {
        header: 'Index type',
      }),
      columnHelper.accessor('entityType', {
        header: 'Entity type',
      }),
      columnHelper.accessor('labelsOrTypes', {
        header: 'Labels or types',
      }),
      columnHelper.accessor('properties', {
        header: 'Properties',
      }),
      columnHelper.accessor('constraintBacking', {
        header: 'Constraint backing',
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <IndexTableActionCell oldIndex={row.original} onFixIndex={() => invalidateIndexesToFixCypherQuery()} />
        ),
        size: 100,
        maxSize: 100,
        enableResizing: false,
      }),
    ];
  }, [invalidateIndexesToFixCypherQuery]);

  const table = useCommonTable({
    enableSorting: true,
    data: oldIndexes,
    columns,
    initialState: {
      sorting: [{ id: 'replacementName', desc: true }],
    },
    enableColumnPinning: true,
    state: {
      columnPinning: {
        right: ['actions'],
      },
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <DataGridHelpers.Wrapper>
      <DataGridHelpers.OuterHeader>
        <div className="flex w-full flex-wrap gap-2">
          <div className="flex basis-[400px] gap-2">
            <SearchField
              className="min-w-36 grow"
              value={globalFilter}
              onChange={(event) => setGlobalFilter(event.target.value)}
              htmlAttributes={{ 'aria-label': 'Search for an' }}
            />
          </div>
          <IconButton ariaLabel="Refresh" onClick={() => invalidateIndexesToFixCypherQuery()}>
            <ArrowPathIconOutline />
          </IconButton>
          {oldIndexes.some((index) => isNullish(index.replacementName)) && (
            <FixAllIndexesButton oldIndexes={oldIndexes} onFixIndex={() => invalidateIndexesToFixCypherQuery()} />
          )}
        </div>
      </DataGridHelpers.OuterHeader>
      <DataGrid isResizable tableInstance={table} isKeyboardNavigable isLoading={isLoading} />
    </DataGridHelpers.Wrapper>
  );
};
